import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Flex from '@utilities/components/Layout/components/Flex';
import { HelpIcon, IconButtonV2, toast } from '@utilities';

import { createChatSession, updateFlowStateTopicIds } from '@api/narrative-projects';
import { readFlow, softDeleteFlowTopic } from '@api/narratives';
import Narratives from '../Narratives';
import ConfirmRemoveTopicModal from './components/ConfirmRemoveTopicModal';
import NarrativesCanvas from './components/NarrativesCanvas';
import NarrativesChatLandingPage from './components/NarrativesChatLandingPage';
import NarrativesChatWindow from './components/NarrativesChatWindow';
import NarrativesNavigation from './components/NarrativesNavigation';
import NarrativesTopicsWindow from './components/NarrativesTopicsWindow';
import { areSameOrderedIds, mergeTopicIds } from './utilities/helpers';

import { HELP_CENTER_BASE_URL } from '../Narratives/utilities/constants';
import { DEFAULT_DRAFT_NARRATIVE_NAME } from './utilities/constants';
import { getRunTopicComponentId } from './utilities/helpers';

import styles from './_index.module.scss';

const PAGES = {
  CHAT: 'chat',
  LANDING: 'landing',
  TEMPLATES: 'templates',
  TOPICS: 'topics',
};

const WORKSPACE_PAGES = new Set([PAGES.CHAT, PAGES.TOPICS]);

const buildInitialSessionState = () => ({
  chatSessionId: null,
});

const NarrativesChat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activePage, setActivePage] = useState(PAGES.LANDING);
  const [flowId, setFlowId] = useState(null);
  const [draftFlowState, setDraftFlowState] = useState(null);
  const [initialInput, setInitialInput] = useState('');
  const [runFlowTopicIds, setRunFlowTopicIds] = useState(null);
  const [sessionState, setSessionState] = useState(buildInitialSessionState);
  const [selectedTopicCards, setSelectedTopicCards] = useState([]);
  const [chatSnapshot, setChatSnapshot] = useState(null);
  const [runTopicPendingDeletion, setRunTopicPendingDeletion] = useState(null);
  const [isDeletingRunTopic, setIsDeletingRunTopic] = useState(false);
  const [runFlowRefreshKey, setRunFlowRefreshKey] = useState(0);
  const [isLeftPaneCollapsed, setIsLeftPaneCollapsed] = useState(false);
  const creatingChatSessionPromiseRef = useRef(null);
  const breakawaySyncRequestIdRef = useRef(0);
  const breakawayAddRequestIdRef = useRef(0);

  const isLanding = activePage === PAGES.LANDING;
  const isTemplates = activePage === PAGES.TEMPLATES;
  const isChat = activePage === PAGES.CHAT;
  const isTopics = activePage === PAGES.TOPICS;
  const shouldDisableBodyScroll = !isLanding && !isTemplates;
  const shouldShowWorkspace = isChat || isTopics;
  const hasSelectedTopics = selectedTopicCards.length > 0;
  const hasWorkspaceContent = Boolean(flowId) || Boolean(draftFlowState) || hasSelectedTopics;
  const shouldAllowCanvasHydrationOnMount = !hasWorkspaceContent;
  const chatSessionIdFromUrl = searchParams.get('sessionId');
  const websocketFlows = useSelector((state) => state?.websocket?.flows);
  const activeRunFlow = useMemo(() => {
    if (!flowId || !Array.isArray(websocketFlows)) return null;
    return websocketFlows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  }, [flowId, websocketFlows]);

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(draftFlowState?.components)) return [];
    return draftFlowState.components.filter((componentId) => componentId != null);
  }, [draftFlowState]);

  const selectedTopicIds = useMemo(() => {
    const manualIds = selectedTopicCards.map((topic) => topic.id).filter((id) => id != null);
    if (flowId) {
      return flowStateTopicIds.length ? flowStateTopicIds : manualIds;
    }
    if (!flowStateTopicIds.length) {
      return manualIds;
    }
    return Array.from(new Set([...flowStateTopicIds, ...manualIds]));
  }, [flowId, flowStateTopicIds, selectedTopicCards]);

  const replaceChatSessionQueryParam = useCallback(
    (chatSessionId) => {
      const params = new URLSearchParams(searchParams);
      if (chatSessionId) {
        params.set('sessionId', chatSessionId);
      } else {
        params.delete('sessionId');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const resetSessionState = useCallback(() => {
    setSessionState(buildInitialSessionState());
    setChatSnapshot(null);
    setFlowId(null);
    setDraftFlowState(null);
    setRunFlowTopicIds(null);
    setIsDeletingRunTopic(false);
    setIsLeftPaneCollapsed(false);
    setRunFlowRefreshKey(0);
    setRunTopicPendingDeletion(null);
    setSelectedTopicCards([]);
    replaceChatSessionQueryParam(null);
  }, [replaceChatSessionQueryParam]);

  const handleChatSessionReady = useCallback(
    (sessionId) => {
      if (!sessionId) return;
      setSessionState((prev) => {
        if (prev.chatSessionId === sessionId) return prev;
        return { ...prev, chatSessionId: sessionId };
      });
      replaceChatSessionQueryParam(sessionId);
      setActivePage((prev) => (prev === PAGES.LANDING ? PAGES.CHAT : prev));
    },
    [replaceChatSessionQueryParam]
  );

  const handleFlowSelected = useCallback((nextFlowId) => {
    setFlowId(nextFlowId);
    setRunFlowTopicIds(null);
  }, []);

  const handleFlowStateChange = useCallback(
    (nextFlowState, options = {}) => {
      if (!options.shouldBreakAwayFromRun || !nextFlowState) {
        setDraftFlowState(nextFlowState);
        return;
      }

      let nextDraftFlowState = nextFlowState;
      const fallbackRunTopicIds = Array.isArray(activeRunFlow?.topics)
        ? activeRunFlow.topics
            .filter((topic) => !topic?.isHidden)
            .map((topic) => getRunTopicComponentId(topic))
            .filter((componentId) => componentId != null)
        : null;
      const baseRunTopicIds = Array.isArray(runFlowTopicIds)
        ? runFlowTopicIds
        : fallbackRunTopicIds;

      if (Array.isArray(baseRunTopicIds) && Array.isArray(nextFlowState.components)) {
        const runComponentIdSet = new Set(baseRunTopicIds.map(String));
        const addedComponentIds = nextFlowState.components.filter(
          (componentId) => !runComponentIdSet.has(String(componentId))
        );
        const nextComponentIds = mergeTopicIds(baseRunTopicIds, addedComponentIds);

        nextDraftFlowState = { ...nextFlowState, components: nextComponentIds };

        if (
          sessionState.chatSessionId &&
          !areSameOrderedIds(nextFlowState.components, nextComponentIds)
        ) {
          const syncRequestId = ++breakawaySyncRequestIdRef.current;
          const expectedComponentsSignature = JSON.stringify(nextComponentIds.map(String));
          updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            topicIds: nextComponentIds,
          })
            .then((updatedFlowState) => {
              if (syncRequestId !== breakawaySyncRequestIdRef.current) return;
              setDraftFlowState((currentFlowState) => {
                const currentComponents = Array.isArray(currentFlowState?.components)
                  ? currentFlowState.components
                  : [];
                if (expectedComponentsSignature !== JSON.stringify(currentComponents.map(String))) {
                  return currentFlowState;
                }
                return updatedFlowState || nextDraftFlowState;
              });
            })
            .catch((error) => {
              console.error('Failed to sync draft flow state after chat breakaway', error);
              toast('We could not sync the draft topic cards. Please refresh and try again.', {
                status: 'error',
              });
            });
        }
      }

      setDraftFlowState(nextDraftFlowState);
      setFlowId(null);
      setSelectedTopicCards([]);
    },
    [activeRunFlow, runFlowTopicIds, sessionState.chatSessionId]
  );

  const ensureChatSession = useCallback(async () => {
    if (sessionState.chatSessionId) return sessionState.chatSessionId;
    if (creatingChatSessionPromiseRef.current) return creatingChatSessionPromiseRef.current;

    const createPromise = (async () => {
      try {
        const sessionId = await createChatSession();
        if (!sessionId) throw new Error('Chat service did not return a session identifier.');
        handleChatSessionReady(sessionId);
        return sessionId;
      } catch (error) {
        console.error('Failed to create chat session from topics workspace', error);
        toast('We could not start your project. Please try again.', { status: 'error' });
        return null;
      } finally {
        creatingChatSessionPromiseRef.current = null;
      }
    })();

    creatingChatSessionPromiseRef.current = createPromise;
    return createPromise;
  }, [handleChatSessionReady, sessionState.chatSessionId]);

  const handleRunFlowTopicIdsChange = useCallback((nextTopicIds = []) => {
    setRunFlowTopicIds((previousTopicIds) => {
      if (!Array.isArray(previousTopicIds)) return nextTopicIds;

      return areSameOrderedIds(previousTopicIds, nextTopicIds) ? previousTopicIds : nextTopicIds;
    });
  }, []);

  const breakAwayToDraftFlowState = useCallback(
    async ({ topic }) => {
      if (!topic?.id) return false;

      const chatSessionId = await ensureChatSession();
      if (!chatSessionId) return false;

      let baseTopicIds = [];
      if (Array.isArray(runFlowTopicIds)) {
        baseTopicIds = runFlowTopicIds;
      } else if (Array.isArray(activeRunFlow?.topics)) {
        baseTopicIds = activeRunFlow.topics
          .filter((topicItem) => !topicItem?.isHidden)
          .map((topicItem) => getRunTopicComponentId(topicItem))
          .filter((componentId) => componentId != null);
      } else if (Array.isArray(draftFlowState?.components)) {
        baseTopicIds = draftFlowState.components;
      }
      const nextComponentIds = mergeTopicIds(baseTopicIds, [topic.id]);
      const previousFlowState = draftFlowState;
      const previousFlowId = flowId;
      const previousSelectedTopicCards = selectedTopicCards;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, components: nextComponentIds }
        : { components: nextComponentIds };
      const requestId = ++breakawayAddRequestIdRef.current;

      setDraftFlowState(optimisticFlowState);
      setFlowId(null);
      setSelectedTopicCards([]);

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId,
            topicIds: nextComponentIds,
          })) || optimisticFlowState;

        if (requestId !== breakawayAddRequestIdRef.current) return false;
        setDraftFlowState(updatedFlowState);
        return true;
      } catch (error) {
        if (requestId !== breakawayAddRequestIdRef.current) return false;
        console.error('Failed to create draft flow state from run Narrative', error);
        setDraftFlowState(previousFlowState);
        setFlowId(previousFlowId);
        setSelectedTopicCards(previousSelectedTopicCards);
        toast('We could not add that topic card. Please try again.', { status: 'error' });
        return false;
      }
    },
    [activeRunFlow, draftFlowState, ensureChatSession, flowId, runFlowTopicIds, selectedTopicCards]
  );

  const handleToggleTopicCard = useCallback(
    async (topic, shouldAdd) => {
      if (!topic?.id) return;

      const topicId = topic.id;

      if (shouldAdd) {
        if (flowId) {
          await breakAwayToDraftFlowState({ topic });
          return;
        }

        setSelectedTopicCards((prev) => {
          const exists = prev.some((item) => item.id === topicId);
          if (exists) return prev;

          const nextTopic = {
            id: topicId,
            name: topic.name,
            vizType: topic.vizType,
          };

          return [...prev, nextTopic];
        });
        return;
      }

      if (flowId) {
        let runTopics = Array.isArray(activeRunFlow?.topics) ? activeRunFlow.topics : [];
        if (!runTopics.length) {
          try {
            const runFlowResponse = await readFlow({ id: flowId });
            runTopics = Array.isArray(runFlowResponse?.topics) ? runFlowResponse.topics : [];
          } catch (error) {
            console.error('Failed to load run topics for topic removal', error);
          }
        }
        const runTopic = runTopics.find(
          (item) => String(getRunTopicComponentId(item)) === String(topicId)
        );
        if (!runTopic?.id) {
          toast.error(
            'We could not find this topic card in the run. Please refresh and try again.'
          );
          return;
        }

        setRunTopicPendingDeletion({ runTopicId: runTopic.id, topicId });
        return;
      }

      setSelectedTopicCards((prev) => prev.filter((item) => item.id !== topicId));

      const currentComponentIds = Array.isArray(draftFlowState?.components)
        ? draftFlowState.components
        : [];
      const nextComponentIds = currentComponentIds.filter((id) => String(id) !== String(topicId));

      if (nextComponentIds.length === currentComponentIds.length) return;

      const previousFlowState = draftFlowState;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, components: nextComponentIds }
        : {
            components: nextComponentIds,
            flowId,
            narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
          };

      setDraftFlowState(optimisticFlowState);

      if (!sessionState.chatSessionId) return;

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            flowId,
            topicIds: nextComponentIds,
          })) || optimisticFlowState;

        setDraftFlowState(updatedFlowState);
      } catch (error) {
        console.error('Failed to remove topic card from draft flow state', error);
        setDraftFlowState(previousFlowState);
        toast.error('We could not remove that topic card. Please try again.');
      }
    },
    [activeRunFlow, breakAwayToDraftFlowState, draftFlowState, flowId, sessionState.chatSessionId]
  );

  const confirmRunTopicDeletion = useCallback(async () => {
    if (!runTopicPendingDeletion?.runTopicId) return;

    setIsDeletingRunTopic(true);
    try {
      await softDeleteFlowTopic({ topicId: runTopicPendingDeletion.runTopicId });
      let runTopics = Array.isArray(activeRunFlow?.topics) ? activeRunFlow.topics : [];
      if (!runTopics.length && flowId) {
        try {
          const runFlowResponse = await readFlow({ id: flowId });
          runTopics = Array.isArray(runFlowResponse?.topics) ? runFlowResponse.topics : [];
        } catch (error) {
          console.error('Failed to load run topics for topic sync after removal', error);
        }
      }

      const nextComponentIdsFromRun = runTopics
        .filter((topic) => String(topic?.id) !== String(runTopicPendingDeletion.runTopicId))
        .map((topic) => getRunTopicComponentId(topic))
        .filter((componentId) => componentId != null);
      const nextComponentIds =
        nextComponentIdsFromRun.length > 0
          ? nextComponentIdsFromRun
          : (Array.isArray(draftFlowState?.components) ? draftFlowState.components : []).filter(
              (componentId) => String(componentId) !== String(runTopicPendingDeletion.topicId)
            );

      const optimisticFlowState = draftFlowState
        ? { ...draftFlowState, components: nextComponentIds }
        : {
            components: nextComponentIds,
            flowId,
            narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
          };
      setDraftFlowState(optimisticFlowState);

      if (sessionState.chatSessionId) {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            flowId,
            topicIds: nextComponentIds,
          })) || optimisticFlowState;

        setDraftFlowState(updatedFlowState);
      }

      setSelectedTopicCards((prev) =>
        prev.filter((item) => String(item?.id) !== String(runTopicPendingDeletion.topicId))
      );
      setRunFlowRefreshKey((prev) => prev + 1);
      setRunTopicPendingDeletion(null);
    } catch (error) {
      console.error('Failed to remove run topic card from topics list', error);
      toast.error('We could not remove that topic card. Please try again.');
    } finally {
      setIsDeletingRunTopic(false);
    }
  }, [activeRunFlow, draftFlowState, flowId, runTopicPendingDeletion, sessionState.chatSessionId]);

  const handlePageChange = useCallback(
    (nextPage) => {
      if (nextPage === 'new-project') {
        resetSessionState();
        setActivePage(PAGES.LANDING);
        return;
      }

      if (nextPage === PAGES.LANDING) {
        resetSessionState();
        setActivePage(PAGES.LANDING);
        return;
      }

      const nextShowsWorkspace = WORKSPACE_PAGES.has(nextPage);
      if (nextShowsWorkspace) {
        if (activePage === nextPage && !isLeftPaneCollapsed) {
          setIsLeftPaneCollapsed(true);
          return;
        }

        setActivePage(nextPage);
        setIsLeftPaneCollapsed(false);
        return;
      }

      if (shouldShowWorkspace) {
        resetSessionState();
      }

      setActivePage(nextPage);
    },
    [activePage, isLeftPaneCollapsed, resetSessionState, shouldShowWorkspace]
  );

  const handleChatMessageSent = useCallback(() => {
    setInitialInput('');
  }, []);

  const handleLandingSubmit = (input) => setInitialInput(input);
  const handleHelpClick = () => {
    window.open(HELP_CENTER_BASE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleChatStateSnapshot = useCallback((snapshot) => {
    setChatSnapshot(snapshot);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', shouldDisableBodyScroll);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [shouldDisableBodyScroll]);

  useEffect(() => {
    if (chatSessionIdFromUrl) {
      setSessionState((prev) => {
        if (prev.chatSessionId === chatSessionIdFromUrl) return prev;
        return { ...prev, chatSessionId: chatSessionIdFromUrl };
      });
      if (activePage === PAGES.LANDING) {
        setActivePage(PAGES.CHAT);
      }
      return;
    }

    if (!sessionState.chatSessionId) return;
    resetSessionState();
    setActivePage(PAGES.LANDING);
  }, [activePage, chatSessionIdFromUrl, resetSessionState, sessionState.chatSessionId]);

  useEffect(() => {
    if (shouldShowWorkspace) return;
    setFlowId(null);
    setDraftFlowState(null);
  }, [shouldShowWorkspace]);

  useEffect(() => {
    if (!hasSelectedTopics || sessionState.chatSessionId) return;
    ensureChatSession();
  }, [ensureChatSession, hasSelectedTopics, sessionState.chatSessionId]);

  const handleCloseToolWindow = useCallback(() => {
    setIsLeftPaneCollapsed(true);
  }, []);

  return (
    <>
      <Flex className={styles['narratives-root']} gap="none">
        <NarrativesNavigation activePage={activePage} onClick={handlePageChange} />

        {isLanding && (
          <>
            <NarrativesChatLandingPage
              onNavigation={handlePageChange}
              onSubmit={handleLandingSubmit}
            />

            <IconButtonV2
              aria-label="Help"
              className={styles['help-button']}
              icon={<HelpIcon />}
              onClick={handleHelpClick}
              size="small"
              tooltip="Help"
              tooltipProps={{ placement: 'left' }}
              variant="subtle"
            />
          </>
        )}

        {isTemplates && <Narratives hasTemplateAccess />}

        {shouldShowWorkspace && (
          <div className={styles['narratives-chat-shell']}>
            <div className={styles['narratives-chat-content']}>
              <div
                aria-hidden={isLeftPaneCollapsed}
                className={styles['narratives-chat-window']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-window"
                inert={isLeftPaneCollapsed ? '' : undefined}
              >
                {isChat && (
                  <NarrativesChatWindow
                    allowCanvasHydrationOnMount={shouldAllowCanvasHydrationOnMount}
                    chatSessionId={sessionState.chatSessionId}
                    initialErrorMessage={chatSnapshot?.errorMessage}
                    initialInput={initialInput}
                    initialInputValue={chatSnapshot?.inputValue}
                    initialMessages={chatSnapshot?.messages}
                    key={sessionState.chatSessionId || 'draft'}
                    onChatSessionReady={handleChatSessionReady}
                    onCloseToolWindow={handleCloseToolWindow}
                    onFlowSelected={handleFlowSelected}
                    onFlowStateChange={handleFlowStateChange}
                    onMessageSent={handleChatMessageSent}
                    onStateSnapshot={handleChatStateSnapshot}
                    shouldBreakAwayFromRunOnMessage={Boolean(flowId)}
                    shouldHydrateOnMount={!chatSnapshot?.wasHydrated}
                    syncCanvasOnMount={!chatSnapshot?.wasHydrated}
                  />
                )}
                {isTopics && (
                  <NarrativesTopicsWindow
                    onCloseToolWindow={handleCloseToolWindow}
                    onToggleTopicCard={handleToggleTopicCard}
                    selectedTopicIds={selectedTopicIds}
                  />
                )}
              </div>

              <div
                className={styles['narratives-chat-canvas']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-canvas"
              >
                <NarrativesCanvas
                  chatSessionId={sessionState.chatSessionId}
                  flowId={flowId}
                  flowState={draftFlowState}
                  onFlowSelected={handleFlowSelected}
                  onFlowStateChange={handleFlowStateChange}
                  onRunFlowTopicIdsChange={handleRunFlowTopicIdsChange}
                  onSelectedTopicsChange={setSelectedTopicCards}
                  runFlowRefreshKey={runFlowRefreshKey}
                  selectedTopicCards={selectedTopicCards}
                />
              </div>
            </div>
          </div>
        )}
      </Flex>
      <ConfirmRemoveTopicModal
        isActive={Boolean(runTopicPendingDeletion)}
        isConfirmDisabled={isDeletingRunTopic}
        onCancel={() => setRunTopicPendingDeletion(null)}
        onConfirm={confirmRunTopicDeletion}
      />
    </>
  );
};

export default NarrativesChat;
